import FloatingLabelRelationshipLink from '../taxonomies/FloatingLabelRelationshipsLink';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../../application-core/constants';
import Errored from '../../core/Errored';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import Loading from '../../core/Loading';
import TitledPanel from '../../core/TitledPanel';
import { makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import LabelLink from '../core/LabelLink';
import FindRelatedLabelDialog from './FindRelatedLabelDialog';

const loadOptions = {};

class LabelRelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      totalRelations: 0,
      unloadedResources: [],
      relatedResources: null,
      isErrored: null
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    // We use the objects internal version counter set by GS to know if 
    if (!state.isLoading && props.values && props.values.length !== state.totalRelations) {
      return {
        relatedResources: null,
        isLoading: true,
        totalRelations: props.values.length
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.relatedResources === null && this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    this.collectResourceData();
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  collectResourceData() {
    if (this.props.values && this.props.values.length > 0) {
      try {
        GS.handle("value-check");
        if (this.props.values && this.props.values[0] === undefined) throw new Error("There is an issue with the data for this label.");
        this.loadData(this.props.values, loadOptions)
          .promise
          .then(() => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
            this.setState({ unloadedResources: doesNotExist });
            this.processResources();
            this.setState({ isLoading: false });
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      }
      catch (e) {
        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
      }
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  processResources() {
    let resources = this.props.values
      .filter(id => GS.isLoaded(id, this.props.graph))
      .map(id => GS.data(id, this.props.graph));
    sortObjects(resources, "literalForm", this.props.defaultLang);
    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ relatedResources: resources });
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "literalForm") this.processResources();
  };

  render() {

    const { components, isLoading, isReadOnly, defaultLang, graph, projectIRI, includeExistingLabel, labelIRI, useWorkflow } = this.props;
    const { relatedResources, unloadedResources, isErrored } = this.state;
    const exclusionList = relatedResources ? relatedResources.map(rr => rr.id) : [];

    if (isErrored) return <Errored dataTest="lr-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <TitledPanel dataTest="label-relation" height="full" title="Label Relation" actions=
        {!isReadOnly ?
          <FindRelatedLabelDialog
            defaultLang={defaultLang}
            useWorkflow={useWorkflow}
            graph={graph}
            exclude={[...exclusionList, labelIRI]}
            onIncludeLabel={includeExistingLabel} />
          :
          null
        }
      >
        {relatedResources && relatedResources.map((rr, index) => {
          if (rr.inScheme && rr.inScheme.length > 0) {
            // Is link within schemes in project?
            const isProjectLink = components && rr.inScheme.some(schemeIRI => components.includes(schemeIRI));
            if (isProjectLink) {
              const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + projectIRI + "&taxonomy="
                + (Array.isArray(rr.inScheme) ? rr.inScheme[0] : rr.inScheme)
                + "&view=label&label=" + rr.id;
              return <LabelLink
                dataTest={"label-link-" + index}
                key={"rel-" + labelIRI + "-" + rr.id}
                link={link}
                id={rr.id}
                graph={graph}
                propertyGraph={null}
                useWorkflow={useWorkflow}
                isReadOnly={isReadOnly}
                source={labelIRI}
                target={rr.id}
                updateEvent={this.monitorLinkUpdate}
                property="xlLabelRelation"
                defaultLang={defaultLang} />;
            }
          }
          else {
            const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + projectIRI + "&view=label&label=" + rr.id;
            return <FloatingLabelRelationshipLink
              id={rr.id}
              link={link}
              key={"rel-" + labelIRI + "-" + rr.id}
              graph={graph}
              isReadOnly={isReadOnly}
              propertyGraph={null}
              source={labelIRI}
              target={rr.id}
              updateEvent={this.monitorLinkUpdate}
              property="xlLabelRelation"
              viewLang={defaultLang}
              defaultLang={defaultLang} />;
          }
        })}
        {unloadedResources && [...unloadedResources].sort().map((iri) =>
          <ExternalResourceLink
            id={iri}
            key={iri}
            graph={graph}
            source={labelIRI}
            isReadOnly={isReadOnly}
            property="xlLabelRelation" />
        )}
      </TitledPanel>
    );
  }
}

LabelRelationshipsDisplay.propTypes = {
  schemeIRI: PropTypes.string,
  graph: PropTypes.string.isRequired,
  useWorkflow: PropTypes.bool,
  labelIRI: PropTypes.string.isRequired,
  components: PropTypes.array,
  defaultLang: PropTypes.string.isRequired,
  includeExistingLabel: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  projectIRI: PropTypes.string.isRequired
};

export default LabelRelationshipsDisplay;